import { ArrowBigRight, CheckCircle, Circle, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import AvatarWithFallback from "./AvatarWithFallback";
import { Card, CardContent, CardHeader, CardTitle } from "~/atoms/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";
import {
  WorkflowStepStatuses,
  type DocumentReleaseSummaryWithWorkflow,
  type WorkflowStep,
} from "~/api/types";
import { formatDate } from "~/helpers/date";
import { StatusBadge } from "./WorkflowStatusBadge";
import { WorkflowStepBadge } from "./WorkflowStepBadge";
import { useUserContext } from "~/hooks/useUserContext";
import { printFullName } from "~/helpers/user";
import { getWorkflowStepTooltip } from "~/helpers/workflow-instances";
import {
  canManageArtifact,
  canPublishArtifact,
  getWorkflowStepControlPermissions,
} from "~/helpers/documentPermissions";
import { useWorkflowStepTransition } from "~/hooks/useWorkflowStepTransition";

import { WorkflowNextStepButton } from "./WorkflowNextStepButton";
import { WorkflowTransitionConfirmDialog } from "./WorkflowTransitionConfirmDialog";

interface WorkflowStepsListProps {
  release: DocumentReleaseSummaryWithWorkflow;
  onChange: () => void;
}

export function WorkflowListSteps({
  release,
  onChange,
}: WorkflowStepsListProps) {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();
  const steps = release.artifact.workflow.steps;
  const {
    isSubmitting,
    confirmMessage,
    requestNextStep,
    moveForward,
    dismissConfirm,
  } = useWorkflowStepTransition({
    documentArtifactId: release.artifact.id,
    onChange,
  });

  const canManage = canManageArtifact(release);
  const canPublish = canPublishArtifact(release);
  const isWorkflowStarted = release.artifact.workflow.isOngoing;

  const showNextStepButton = (step: WorkflowStep, stepIndex: number) => {
    const { canMoveForward, showControls } = getWorkflowStepControlPermissions({
      canManage,
      canPublish,
      isWorkflowStarted,
      isActiveStep: step.status === WorkflowStepStatuses.active,
      isFirstStep: stepIndex === 0,
      isLastStep: stepIndex === steps.length - 1,
      artifactStatus: release.artifact.status,
    });

    return showControls && canMoveForward;
  };

  return (
    <div className="relative">
      <div className="flex snap-x gap-3">
        {steps.map((step, stepIndex) => (
          <div key={step.id} className="flex shrink-0 items-center gap-3">
            <Card
              className={`border-t-4 ${getStepBorderColor(step.status)} w-70 shrink-0 snap-start py-2`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {getStepStatusIcon(step.status)}
                      <CardTitle className="text-sm font-medium">
                        {step.name}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={step.status} />
                      {(step.status === WorkflowStepStatuses.active ||
                        step.status === WorkflowStepStatuses.upcoming) &&
                        step.dueAt && (
                          <span className="text-muted-foreground text-xs">
                            {t(($) => $.commons.workflow.stepDueAt, {
                              date: formatDate(step.dueAt),
                            })}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-2 py-0">
                {step.users.length > 0 ? (
                  <div className="space-y-1">
                    <span className="text-xs font-medium">
                      {t(($) => $.commons.workflow.assigned)}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {step.users.map((user) => {
                        const isCurrentUser = user.userId === currentUser.id;
                        const isActive =
                          step.status === WorkflowStepStatuses.active;
                        const response = user.responseStatus;
                        const tooltip = getWorkflowStepTooltip(
                          response,
                          user,
                          isCurrentUser,
                          t,
                        );

                        return (
                          <TooltipProvider key={user.userId}>
                            <Tooltip>
                              <TooltipTrigger className="cursor-help">
                                <WorkflowStepBadge
                                  response={response}
                                  isActive={isActive}
                                >
                                  <AvatarWithFallback user={user} size="xs" />
                                  <span className="font-normal">
                                    {printFullName(
                                      { firstName: user.firstName },
                                      isCurrentUser,
                                    )}
                                  </span>
                                </WorkflowStepBadge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground text-xs italic">
                    {t(($) => $.commons.workflow.noUsersAssigned)}
                  </div>
                )}
              </CardContent>
            </Card>
            {showNextStepButton(step, stepIndex) && (
              <WorkflowNextStepButton
                step={step}
                isSubmitting={isSubmitting}
                onNextStep={() => requestNextStep(step)}
                size="sm"
                icon={ArrowBigRight}
              />
            )}
          </div>
        ))}
      </div>
      <WorkflowTransitionConfirmDialog
        message={confirmMessage}
        onConfirm={moveForward}
        onDismiss={dismissConfirm}
      />
    </div>
  );
}

function getStepStatusIcon(status: string) {
  switch (status) {
    case WorkflowStepStatuses.completed:
      return <CheckCircle className="text-success size-3.5" />;
    case WorkflowStepStatuses.active:
      return <Clock className="text-info size-3.5" />;
    case WorkflowStepStatuses.upcoming:
      return <Circle className="text-muted-foreground size-3.5" />;
    default:
      return null;
  }
}

function getStepBorderColor(status: string): string {
  switch (status) {
    case WorkflowStepStatuses.completed:
      return "border-success";
    case WorkflowStepStatuses.active:
      return "border-info";
    case WorkflowStepStatuses.upcoming:
      return "border-border";
    default:
      return "border-border";
  }
}
